import Stripe from "stripe";

import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import { IDrop } from "src/core/drops/api/drops.api";
import useAxios from "src/core/shared/hooks/useAxios";
import {
  EndpointConfig,
  UseApiRequest,
  RequestConfig
} from "src/core/shared/api/config.api";
import { PaginatedData } from "src/core/shared/api";

type TransactionEndpointConfig = {
  LIST_TRANSACTIONS: EndpointConfig<
    ListTransactionsParams,
    PaginatedData<PaymentTransaction>
  >;
  GET_TRANSACTION: EndpointConfig<
    number,
    PaymentTransaction
  >;
};

const requests: RequestConfig<TransactionEndpointConfig> = {
  LIST_TRANSACTIONS: params => {
    return {
      url: `${process.env.REACT_APP_API_URL}/payment/transactions`,
      method: "GET",
      params
    };
  },
  GET_TRANSACTION: transactionId => {
    return {
      url: `${process.env.REACT_APP_API_URL}/payment/transactions/${transactionId}`,
      method: "GET"
    };
  }
};

export const useTransactionApi: UseApiRequest<TransactionEndpointConfig> = name => useAxios(requests[ name ], name);

export type PartialTransaction = Partial<PaymentTransaction>;
export type ListTransactionsParams<T extends PartialTransaction = PaymentTransaction> = {
  pagination: PaginatedParams<T>;
  filters: FilterParams<PaymentTransaction>;
};

export interface PaymentTransaction {
  id: number;
  provider: TransactionProvider;
  externalId: string | null;
  externalEntityId?: string | null;
  branchId: number;
  branch?: IDrop;
  orderId: number;
  paymentCardId: number;
  amount: number;
  type: TransactionType;
  session?: TransactionSessionType | null;
  status: TransactionStatus;
  nextAction?: TransactionNextActionAttributes | null;
  errorReason?: TransactionErrorAttributes | null;
  message?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export enum TransactionProvider {
  "STRIPE" = "stripe"
}

export enum TransactionType {
  "CHARGE" = "charge",
  "REFUND" = "refund"
}

export enum TransactionStatus {
  "REQUIRES_PAYMENT_METHOD" = "requires_payment_method",
  "REQUIRES_CONFIRMATION" = "requires_confirmation",
  "REQUIRES_ACTION" = "requires_action",
  "REQUIRES_CAPTURE" = "requires_capture",
  "PROCESSING" = "processing",
  "CANCELED" = "canceled",
  "SUCCEEDED" = "succeeded",
  "FAILED" = "failed"
}

export enum TransactionSessionType {
  "ON_SESSION" = "on_session",
  "OFF_SESSION" = "off_session"
}

export interface TransactionNextActionAttributes {
  type: TransactionNextActionType;
  action: TransactionNextActionActionAttributes;
}

export enum TransactionNextActionType {
  "THREE_D_SECURE" = "three_d_secure",
  "REDIRECT" = "redirect",
}

export interface TransactionNextActionActionAttributes {
  url: string;
  source?: string;
}

export interface TransactionErrorAttributes {
  type: Stripe.RawErrorType;
  code: TransactionErrorCode;
  message: string;
}

// Stripe's error codes plus our own customer ones
export type TransactionErrorCode = StripeErrorCode | "invalid_possession" | "unknown";

// Stripe's error codes are loosely typed (string), their documentation informs
// us of what these should be
export type StripeErrorCode =
  | "authentication_required"
  | "approve_with_id"
  | "call_issuer"
  | "card_not_supported"
  | "card_velocity_exceeded"
  | "currency_not_supported"
  | "do_not_honor"
  | "do_not_try_again"
  | "duplicate_transaction"
  | "expired_card"
  | "fraudulent"
  | "generic_decline"
  | "incorrect_number"
  | "incorrect_cvc"
  | "incorrect_pin"
  | "incorrect_zip"
  | "insufficient_funds"
  | "invalid_account"
  | "invalid_amount"
  | "invalid_cvc"
  | "invalid_expiry_month"
  | "invalid_expiry_year"
  | "invalid_number"
  | "invalid_pin"
  | "issuer_not_available"
  | "lost_card"
  | "merchant_blacklist"
  | "new_account_information_available"
  | "no_action_taken"
  | "not_permitted"
  | "offline_pin_required"
  | "online_or_offline_pin_required"
  | "pickup_card"
  | "pin_try_exceeded"
  | "processing_error"
  | "reenter_transaction"
  | "restricted_card"
  | "revocation_of_all_authorizations"
  | "revocation_of_authorization"
  | "security_violation"
  | "service_not_allowed"
  | "stolen_card"
  | "stop_payment_order"
  | "testmode_decline"
  | "transaction_not_allowed"
  | "try_again_later"
  | "withdrawal_count_limit_exceeded";

export interface StripeThreeDSecureEventAttributes {
  type: "stripe-3ds-result" | "3DS-authentication-complete" | "3DS-authentication-error",
  message: StripeThreeDSecureEventMessageAttributes;
}

export interface StripeThreeDSecureEventMessageAttributes {
  stripeError?: Stripe.StripeRawError;
  payment_intent?: Stripe.PaymentIntent;
  transaction?: PaymentTransaction;
}

export enum TransactionErrorAction {
  "CONTACT_CARD_ISSUER" = "contact_card_issuer",
  "ALTERNATE_PAYMENT_REQUIRED" = "alternate_payment_required",
  "TRY_AGAIN" = "try_again"
}
